import { Link } from 'react-router-dom';

import { BookCover } from '@/features/library/components/BookCover.js';
import type { ShelfEntryView } from '@/features/library/api/library.api.js';
import { useMyShelf } from '@/features/library/hooks/useMyShelf.js';
import { useUpdateShelfEntry } from '@/features/library/hooks/useUpdateShelfEntry.js';

export function ReadingDisplay(): JSX.Element {
  const { data, isLoading } = useMyShelf('reading');

  return (
    <section className="flex flex-col gap-4">
      <div>
        <h2 className="font-serif text-2xl font-semibold text-brown">
          Tu lis en ce moment
        </h2>
        <p className="font-script text-lg text-brown-soft">déjà bien avancée ✨</p>
      </div>

      {isLoading && <p className="text-sm text-brown-soft">Chargement…</p>}

      {!isLoading && (!data || data.items.length === 0) && (
        <div className="shelf-card rounded-3xl bg-paper p-6 pb-7 shadow-md">
          <p className="text-sm text-brown-soft">
            Pas de lecture en cours. Pioche dans ta wishlist 🌸
          </p>
          <Link
            to="/library"
            className="mt-3 inline-block text-sm font-semibold text-terracotta hover:underline"
          >
            → Voir mes étagères
          </Link>
        </div>
      )}

      {data && data.items[0] && <ReadingCard entry={data.items[0]} />}
    </section>
  );
}

function formatRelative(iso: string): string {
  const date = new Date(iso);
  const diffMs = Date.now() - date.getTime();
  const diffH = Math.floor(diffMs / 3_600_000);
  if (diffH < 1) return 'mise à jour à l’instant';
  if (diffH < 24) return `mise à jour il y a ${diffH} h`;
  const diffD = Math.floor(diffH / 24);
  if (diffD < 7) return `mise à jour il y a ${diffD} j`;
  return `mise à jour le ${date.toLocaleDateString('fr-FR')}`;
}

function ReadingCard({ entry }: { entry: ShelfEntryView }): JSX.Element {
  const mutation = useUpdateShelfEntry();
  const { book } = entry;
  const progress =
    entry.currentPage !== null && book.pages !== null && book.pages > 0
      ? Math.min(100, Math.round((entry.currentPage / book.pages) * 100))
      : null;

  return (
    <article className="shelf-card flex gap-5 rounded-3xl bg-paper p-5 pb-7 shadow-md md:p-6 md:pb-8">
      <BookCover
        url={book.coverUrl}
        title={book.title}
        book={book}
        className="w-24 flex-shrink-0 md:w-28"
      />
      <div className="flex flex-1 flex-col justify-between gap-3 overflow-hidden">
        <div>
          <span className="inline-block rounded-full bg-sage-light px-3 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-sage-dark">
            {book.genre}
          </span>
          <h3 className="mt-2 font-serif text-lg font-semibold leading-tight text-brown md:text-xl">
            {book.title}
          </h3>
          <p className="text-sm text-brown-soft">{book.author}</p>
        </div>

        {/* Citation surlignée — placeholder, viendra des annotations Kindle */}
        <p className="reading-highlight">
          « La peur est un poison, mais elle peut aussi être un carburant. »
        </p>

        {progress !== null && (
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center gap-3">
              <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-cream-dark">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-terracotta-light to-terracotta transition-all"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <span className="text-xs font-semibold text-brown">
                {progress}% · p. {entry.currentPage}
              </span>
            </div>
            <p className="text-[11px] text-brown-light">
              {formatRelative(entry.updatedAt)} · {book.pages} pages au total
            </p>
          </div>
        )}

        <button
          type="button"
          disabled={mutation.isPending}
          onClick={() => mutation.mutate({ entryId: entry.id, status: 'done' })}
          className="self-start rounded-full bg-cream-dark px-3 py-1.5 text-xs font-semibold text-brown transition-colors hover:bg-terracotta hover:text-cream-light disabled:opacity-50"
        >
          ✅ Marquer comme terminé
        </button>
      </div>
    </article>
  );
}
